/**
 * Community Detail Page
 * Full community profile view with image carousel, info section, and tabbed content
 */

import { Loader2, Search, X } from "lucide-react"
import { useDeferredValue, useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { GuestAction } from "@/components/guest"
import { ArrowLeftIcon, StarIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"

import { useTranslation } from "@/language"
import { CommunityImageCarousel } from "../components/communityImageCarousel"
import { CommunityInfoSection } from "../components/communityInfoSection"
import { DiscussionTab } from "../components/discussionTab"
import { JoinRequestsSheet } from "../components/JoinRequestsSheet"
import { MembersTab } from "../components/membersTab"
import { RitualistsTab } from "../components/ritualistsTab"
import { RitualsTab } from "../components/ritualsTab"
import { useCommunity, useCommunityStatistics } from "../hooks/useCommunity"
import { useCommunityJoinActions } from "../hooks/useCommunityMembership"
import { useCommunityVideos } from "../hooks/useCommunityVideos"
import type { CarouselMediaItem, CommunityDetail } from "../types/community-detail"

interface ApiCommunityMember {
  id: string
  userId: string
  email?: string | null
  name?: string | null
  username?: string | null
  profilePictureUrl?: string | null
  communityBuilder?: boolean
}

type CommunityTab = "rituals" | "discussion" | "ritualists" | "members"

export function CommunityDetailScreen() {
  const { t } = useTranslation("communities")
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get("tab") as CommunityTab) || "rituals"
  const [activeTab, setActiveTab] = useState<CommunityTab>(initialTab)
  const { join, isJoining } = useCommunityJoinActions()
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showLoginModal, setShowLoginModal] = useState(false)
  const deferredSearchQuery = useDeferredValue(searchQuery)

  const tabTriggerClass =
    "!flex-none !rounded-none !h-[65px] !px-5 !text-[15px] !font-semibold !uppercase !whitespace-nowrap !leading-[25px] !cursor-pointer !border-0 !border-b !bg-transparent after:!hidden data-[state=active]:!text-charcoal data-[state=active]:!border-charcoal data-[state=inactive]:!text-charcoal/50 data-[state=inactive]:!border-charcoal/20"

  // Get current user ID from auth store
  const currentUserId = useAuthStore(state => state.user?.id)
  const isGuest = !useAuthStore(state => state.user)

  // Fetch real community data
  const { data: apiCommunity, isLoading, isError } = useCommunity(id || "")

  // Native (Cloudflare) videos, merged into the media carousel below (parity
  // with rituals). Access-gated: a 403/404 surfaces as an empty list.
  const { data: nativeVideos = [] } = useCommunityVideos(id, { enabled: !isGuest })

  useEffect(() => {
    if (!isLoading && initialTab !== "rituals") {
      const el = document.getElementById("community-tabs")
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100)
      }
    }
  }, [isLoading, initialTab])

  // Fetch community statistics (only for public communities or joined members)
  const { data: statistics } = useCommunityStatistics(id || "", {
    enabled: Boolean(id && (apiCommunity?.type === "PUBLIC" || apiCommunity?.isJoined)),
  })

  // Handle switching to members tab and scrolling to it
  const handleViewMembers = () => {
    setActiveTab("members")
    // Scroll to the tabs section after tab switch
    setTimeout(() => {
      const el = document.getElementById("community-tabs")
      if (el) {
        el.scrollIntoView({ behavior: "smooth" })
      }
    }, 100)
  }

  // Listen for join click from RitualsTab empty state
  useEffect(() => {
    const handleJoinClick = () => {
      if (id) {
        join(id)
      }
    }
    window.addEventListener("community-join-click", handleJoinClick)
    return () => window.removeEventListener("community-join-click", handleJoinClick)
  }, [join, id])

  // Transform API data to match CommunityDetail type expected by components
  const community: CommunityDetail | null = apiCommunity
    ? {
        id: apiCommunity.id,
        slug: (apiCommunity as unknown as Record<string, unknown>).slug as string | undefined,
        name: apiCommunity.name,
        handle: apiCommunity.handle,
        type: apiCommunity.type,
        description: apiCommunity.description,
        guidelines: apiCommunity.guidelines,
        // Format location from city, state, country
        location:
          [apiCommunity.city, apiCommunity.state, apiCommunity.country]
            .filter(Boolean)
            .join(", ") || undefined,
        // Social handles
        websiteUrl: apiCommunity.websiteUrl || undefined,
        twitterHandle: apiCommunity.twitterUrl || undefined,
        instagramHandle: apiCommunity.instagramUrl || undefined,
        facebookUrl: apiCommunity.facebookUrl || undefined,
        avatarUrl: undefined,
        // Build coverImages from unified media array
        coverImages:
          apiCommunity.media
            ?.filter(m => m.mediaType === "IMAGE")
            .sort((a, b) => (b.isCover ? 1 : 0) - (a.isCover ? 1 : 0))
            .map(img => img.filePath) || [],
        // Tags - preserve object structure { id, name }
        tags: apiCommunity.tags || [],
        // Member count from API field, fallback to array length
        memberCount: apiCommunity.totalMemberCount ?? apiCommunity.members?.length ?? 0,
        // Transform members to match CommunityMember interface
        members:
          apiCommunity.members?.map((member: ApiCommunityMember) => ({
            id: member.id,
            userId: member.userId, // Use userId from backend for navigation
            name: member.name || "",
            username: member.username || member.email || "", // Fallback to email if username not available
            avatarUrl: member.profilePictureUrl || undefined, // Use profilePictureUrl from backend
            communityBuilder: member.communityBuilder || false, // Use camelCase
          })) || [],
        // Empty arrays for features without backend implementation
        rituals: [],
        announcements: [],
        discussions: [], // Removed mock data - discussions now come from API if available
        coaches: [], // Removed - no longer used
        createdBy: apiCommunity.createdBy,
        createdAt: apiCommunity.createdAt
          ? new Date(apiCommunity.createdAt).toISOString()
          : undefined,
        // Membership and admin status (from API if available)
        isJoined: apiCommunity.isJoined ?? undefined,
        isPending: apiCommunity.isPending ?? undefined,
        isModerator: apiCommunity.isModerator ?? false,
        isSaved: apiCommunity.isSaved ?? false,
        pendingRequestsCount: apiCommunity.pendingRequestsCount ?? 0,
        permissions: apiCommunity.permissions ?? {
          isOwner: false,
          canManageMembers: false,
          canAddRituals: false,
          canComment: false,
          canReply: false,
        },
      }
    : null

  // Build carousel media from unified media array
  // Sort by sortOrder ASC, then ensure cover image is first
  const carouselMedia: CarouselMediaItem[] = apiCommunity
    ? (() => {
        const items = [...(apiCommunity.media || [])]
        // Sort by sortOrder
        items.sort((a, b) => a.sortOrder - b.sortOrder)
        // Move cover image to front
        const coverIndex = items.findIndex(m => m.isCover)
        if (coverIndex > 0) {
          const [cover] = items.splice(coverIndex, 1)
          items.unshift(cover)
        }
        const mediaItems: CarouselMediaItem[] = items.map(item => {
          if (item.mediaType === "IMAGE") {
            return { type: "image" as const, url: item.filePath }
          }
          return {
            type: "video" as const,
            url: item.filePath,
            thumbnailUrl: item.thumbnailFilePath || null,
            title: item.videoTitle,
          }
        })
        // Append native (Cloudflare) videos that are ready to play.
        const nativeItems: CarouselMediaItem[] = nativeVideos
          .filter(v => v.status === "ready" && v.cloudflareUid !== null)
          .map(v => ({ type: "native-video" as const, video: v }))
        return [...mediaItems, ...nativeItems]
      })()
    : []

  const tabs: { value: CommunityTab; label: string; count: number }[] = [
    { value: "rituals", label: t("tabs.rituals"), count: statistics?.ritualsAll ?? 0 },
    { value: "discussion", label: t("tabs.discussion"), count: statistics?.discussions ?? 0 },
    { value: "ritualists", label: t("tabs.ritualists"), count: statistics?.ritualists ?? 0 },
    { value: "members", label: t("tabs.members"), count: community?.memberCount ?? 0 },
  ]

  if (!id) {
    return <div>{t("communityDetail.notFound")}</div>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone">{t("communityDetail.loading")}</p>
      </div>
    )
  }

  if (isError || !community) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-red">{t("communityDetail.error")}</p>
      </div>
    )
  }

  return (
    <div className="w-full mt-5">
      <div className="relative">
        {/* Moderator Join Requests Button - Only show for moderators with pending requests */}
        {community.isModerator && (community.pendingRequestsCount ?? 0) > 0 && (
          <div className="absolute top-20 right-5 md:right-10">
            <JoinRequestsSheet communityId={id || ""} />
          </div>
        )}
      </div>

      {/* Image Carousel */}
      <div className="relative">
        <CommunityImageCarousel
          media={carouselMedia}
          communityId={id}
          leadingElement={
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="hidden md:flex p-5 rounded-md border-gradient-glass hover:border-gradient-glass"
            >
              <ArrowLeftIcon size="5" color="white" />
            </Button>
          }
        />
      </div>
      <div className="pt-3.5 md:pt-13  px-5 md:px-17.5 max-w-360 mx-auto">
        {/* Info Section */}
        <CommunityInfoSection
          community={community}
          currentUserId={currentUserId}
          onViewMembers={handleViewMembers}
          isGuest={isGuest}
        />
      </div>

      {/* Enter the Circle - Promotional Box */}
      {!community.isJoined && !community.isPending && (
        <div className="pt-7.5 md:pt-10 max-w-360 mx-auto px-5 md:px-17.5">
          <div
            className="flex flex-col md:flex-row items-center justify-between p-5 rounded-xl border"
            style={{ backgroundColor: "#C9A8761A", borderColor: "#C9A8761A" }}
          >
            <div className="flex md:items-center gap-4">
              <div
                className="flex items-center justify-center min-w-12 md:w-12 h-12 rounded-full"
                style={{ backgroundColor: "#C9A87626" }}
              >
                <StarIcon className="text-off-white" />
              </div>
              <div className="space-y-1 text-center md:text-left">
                {community.type === "PRIVATE" ? (
                  <>
                    <p className="text-[16px] font-semibold text-primary font-monteserat leading-[20px]">
                      This is a private community
                    </p>
                    <p className="text-[14px] text-stone mt-1 font-monteserat leading-[20px] font-normal">
                      You need an invitation or approval to access this community.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[16px] font-semibold text-primary font-monteserat leading-[20px]">
                      Every community begins with a small circle
                    </p>
                    <p className="text-[14px] text-stone mt-1 font-monteserat leading-[20px] font-normal">
                      Join early and help create the traditions that others will follow.
                    </p>
                  </>
                )}
              </div>
            </div>
            {isGuest ? (
              <GuestAction>
                <Button
                  variant="primary"
                  size="sm"
                  className="rounded-full mt-4 md:mt-0 w-full md:w-auto"
                >
                  {community.type === "PRIVATE" ? "Request to Join" : "Enter the Circle"}
                </Button>
              </GuestAction>
            ) : (
              <Button
                onClick={() => join(community.id)}
                disabled={isJoining}
                variant="primary"
                size="sm"
                className="rounded-full mt-4 md:mt-0 w-full md:w-auto"
              >
                {isJoining ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : community.type === "PRIVATE" ? (
                  "Request to Join"
                ) : (
                  "Enter the Circle"
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      <div>
        {/* Only show tabs for public communities OR for joined/pending members of private communities */}
        {community.type === "PUBLIC" || community.isJoined ? (
          <Tabs
            id="community-tabs"
            value={activeTab}
            onValueChange={v => {
              setActiveTab(v as CommunityTab)
              setShowSearch(false)
              setSearchQuery("")
            }}
          >
            {/* Tab bar */}
            <div className="flex items-center max-w-360 md:px-17.5 mx-auto w-full overflow-x-auto scrollbar-hide mt-7.5 md:mt-7">
              <TabsList
                variant="default"
                className="!h-auto !gap-0 p-0 flex-row bg-transparent shrink-0"
              >
                {tabs.map(tab => (
                  <TabsTrigger key={tab.value} value={tab.value} className={tabTriggerClass}>
                    <span className="whitespace-nowrap">{tab.label} </span>
                    <span className="font-normal whitespace-nowrap">({tab.count})</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Spacer with bottom border */}
              <div className="flex-1 h-[65px] border-b border-charcoal/20" />

              {/* Search toggle */}
              <div className="hidden md:flex items-center h-[65px] border-b border-charcoal/20 shrink-0">
                {showSearch ? (
                  <div className="flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5">
                    <Search className="w-4 h-4 text-ash shrink-0" />
                    <Input
                      type="text"
                      placeholder={t("searchPlaceholder")}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-48 border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-6 w-6 text-ash"
                      onClick={() => {
                        setShowSearch(false)
                        setSearchQuery("")
                      }}
                      aria-label="Close search"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="opacity-50 cursor-pointer"
                    onClick={() => (isGuest ? setShowLoginModal(true) : setShowSearch(true))}
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5 text-charcoal" />
                  </button>
                )}
              </div>
            </div>

            {/* Tab content area */}
            <div className="md:bg-card">
              <div
                className={`py-5 md:py-0 max-w-360 mx-auto md:px-17.5 ${activeTab !== "members" ? " px-5" : ""}`}
              >
                <TabsContent value="rituals">
                  <RitualsTab
                    communityId={id}
                    searchQuery={searchQuery}
                    isJoined={community.isJoined}
                    isPending={community.isPending}
                    permissions={community.permissions}
                    whoCanAddRituals={community.whoCanAddCommunityToRituals}
                    showSearch={showSearch}
                    onToggleSearch={() => setShowSearch(s => !s)}
                    onSearchChange={setSearchQuery}
                    isGuest={isGuest}
                  />
                </TabsContent>

                <TabsContent value="discussion">
                  <h2 className="text-lg leading-7.5 font-semibold text-primary uppercase md:hidden">
                    Discussion
                  </h2>
                  <DiscussionTab
                    entityId={community.id}
                    searchQuery={deferredSearchQuery}
                    canComment={community.permissions?.canComment ?? false}
                    canModerate={Boolean(
                      community.permissions?.isOwner || community.permissions?.canManageMembers,
                    )}
                    headingClassName="text-lg pt-0"
                    isGuest={isGuest}
                  />
                </TabsContent>

                <TabsContent value="ritualists">
                  <RitualistsTab
                    communityId={id}
                    searchQuery={searchQuery}
                    permissions={community.permissions}
                    currentUserId={currentUserId}
                    creatorId={community.createdBy}
                    showSearch={showSearch}
                    onToggleSearch={() => setShowSearch(s => !s)}
                    onSearchChange={setSearchQuery}
                    isGuest={isGuest}
                  />
                </TabsContent>

                <TabsContent value="members">
                  <MembersTab
                    communityId={id}
                    searchQuery={searchQuery}
                    permissions={community.permissions}
                    currentUserId={currentUserId}
                    creatorId={community.createdBy}
                    showSearch={showSearch}
                    onToggleSearch={() => setShowSearch(s => !s)}
                    onSearchChange={setSearchQuery}
                    isGuest={isGuest}
                  />
                </TabsContent>
              </div>
            </div>
          </Tabs>
        ) : (
          <div className="w-full min-h-[400px]" />
        )}
      </div>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
